import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { PinProvider, usePin } from './context/PinContext';
import { Toaster } from './components/ui/sonner';

// Pages
import PinSetupPage from './pages/PinSetupPage';
import PinUnlockPage from './pages/PinUnlockPage';
import DashboardPage from './pages/DashboardPage';
import FirearmsPage from './pages/FirearmsPage';
import FirearmDetailPage from './pages/FirearmDetailPage';
import FirearmFormPage from './pages/FirearmFormPage';
import AmmunitionPage from './pages/AmmunitionPage';
import AmmunitionFormPage from './pages/AmmunitionFormPage';
import AccessoriesPage from './pages/AccessoriesPage';
import AccessoryFormPage from './pages/AccessoryFormPage';
import DocumentsPage from './pages/DocumentsPage';
import DocumentFormPage from './pages/DocumentFormPage';
import WishlistPage from './pages/WishlistPage';
import WishlistFormPage from './pages/WishlistFormPage';
import SettingsPage from './pages/SettingsPage';

import './App.css';

// Protected Route wrapper - requires PIN unlock
const ProtectedRoute = ({ children }) => {
  const { isUnlocked, isPinSet, isLoading } = usePin();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-foreground">Loading...</div>
      </div>
    );
  }

  // If no PIN set, redirect to setup
  if (!isPinSet) {
    return <Navigate to="/setup-pin" state={{ from: location }} replace />;
  }

  // If PIN set but not unlocked, redirect to unlock
  if (!isUnlocked) {
    return <Navigate to="/unlock" state={{ from: location }} replace />;
  }

  return children;
};

// App Router component
const AppRouter = () => {
  const { isPinSet, isUnlocked, isLoading } = usePin();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <Routes>
      {/* PIN routes */}
      <Route path="/setup-pin" element={<PinSetupPage />} />
      <Route path="/unlock" element={<PinUnlockPage />} />
      
      {/* Root redirect */}
      <Route 
        path="/" 
        element={
          !isPinSet 
            ? <Navigate to="/setup-pin" replace /> 
            : !isUnlocked 
            ? <Navigate to="/unlock" replace />
            : <Navigate to="/dashboard" replace />
        } 
      />

      {/* Protected routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      
      {/* Firearms */}
      <Route path="/firearms" element={<ProtectedRoute><FirearmsPage /></ProtectedRoute>} />
      <Route path="/firearms/new" element={<ProtectedRoute><FirearmFormPage /></ProtectedRoute>} />
      <Route path="/firearms/:id" element={<ProtectedRoute><FirearmDetailPage /></ProtectedRoute>} />
      <Route path="/firearms/:id/edit" element={<ProtectedRoute><FirearmFormPage /></ProtectedRoute>} />
      
      {/* Ammunition */}
      <Route path="/ammunition" element={<ProtectedRoute><AmmunitionPage /></ProtectedRoute>} />
      <Route path="/ammunition/new" element={<ProtectedRoute><AmmunitionFormPage /></ProtectedRoute>} />
      <Route path="/ammunition/:id" element={<ProtectedRoute><AmmunitionFormPage /></ProtectedRoute>} />
      <Route path="/ammunition/:id/edit" element={<ProtectedRoute><AmmunitionFormPage /></ProtectedRoute>} />
      
      {/* Accessories */}
      <Route path="/accessories" element={<ProtectedRoute><AccessoriesPage /></ProtectedRoute>} />
      <Route path="/accessories/new" element={<ProtectedRoute><AccessoryFormPage /></ProtectedRoute>} />
      <Route path="/accessories/:id" element={<ProtectedRoute><AccessoryFormPage /></ProtectedRoute>} />
      <Route path="/accessories/:id/edit" element={<ProtectedRoute><AccessoryFormPage /></ProtectedRoute>} />
      
      {/* Documents */}
      <Route path="/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
      <Route path="/documents/new" element={<ProtectedRoute><DocumentFormPage /></ProtectedRoute>} />
      <Route path="/documents/:id" element={<ProtectedRoute><DocumentFormPage /></ProtectedRoute>} />
      <Route path="/documents/:id/edit" element={<ProtectedRoute><DocumentFormPage /></ProtectedRoute>} />
      
      {/* Wishlist */}
      <Route path="/wishlist" element={<ProtectedRoute><WishlistPage /></ProtectedRoute>} />
      <Route path="/wishlist/new" element={<ProtectedRoute><WishlistFormPage /></ProtectedRoute>} />
      <Route path="/wishlist/:id" element={<ProtectedRoute><WishlistFormPage /></ProtectedRoute>} />
      <Route path="/wishlist/:id/edit" element={<ProtectedRoute><WishlistFormPage /></ProtectedRoute>} />
      
      {/* Settings */}
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />

      {/* Catch all - redirect based on PIN state */}
      <Route 
        path="*" 
        element={
          !isPinSet 
            ? <Navigate to="/setup-pin" replace /> 
            : !isUnlocked 
            ? <Navigate to="/unlock" replace />
            : <Navigate to="/dashboard" replace />
        } 
      />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <PinProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="bottom-right" richColors />
        </BrowserRouter>
      </PinProvider>
    </ThemeProvider>
  );
}

export default App;
